import { push } from "connected-react-router";
import { useState, useMemo, useCallback } from "react";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import {
  APPLICATION_MODE_NEW,
  APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE,
  APPLICATION_TYPE_MORTGAGE_PRINCIPAL_BROKER_LICENCE,
  ROUTE_PATH,
  VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT,
  LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_NEW_APPLICATION,
  LICENCE_SUB_TYPE_MORTGAGE_BROKER_NEW_APPLICATION,
  DOCUMENT_TYPE_CRIMINAL_RECORD,
  DOCUMENT_CONTENT_TYPE_PDF,
  DOCUMENT_TYPE_EDUCATION_CERTIFICATE,
  DOCUMENT_TYPE_WORK_EXPERIENCE,
  LICENCE_SUB_TYPE_PRINCIPAL_BROKER_NEW_APPLICATION,
  LICENCE_SUB_TYPE_PRINCIPAL_BROKER_RENEWAL_APPLICATION,
  DOCUMENT_TYPE_BROKER_COURSE_CERTIFICATE
} from "../../app/constants";
import {
  Header,
  Button,
  Paragraph,
  ErrorMark,
  NavigationLinkButton
} from "../../components/RegularComponents";
import {
  DocumentViewerFileNameLink,
  DownloadFileNameLink
} from "../../components/DocumentViewer/DocumentFileComponent";
import { convertFileDataForUrl } from "../../app/documentUploadUtils";
import DocumentViewer from "../../components/DocumentViewer/DocumentViewer";
import { MraSelectPrincipalBroker } from "./MraSelectPrincipalBroker";
import { PreviouslyLicencedMortgageBroker } from "./PreviouslyLicencedMortgageBroker";
import { ProvideBrokerContactInformation } from "./ProvideBrokerContactInformation";
import { MortgageBrokerInfo } from "./MortgageBrokerInfo";
import { ConfirmCompletionRegulatoryInfo } from "./ConfirmCompletionRegulatoryInfo";
import { AssociateBrokerAddApplicant } from "./MRA-Broker-Associate/AssociateBrokerAddApplicant";
import { UploadCriminalRecordCheck } from "./UploadCriminalRecordCheck";
import { UploadWorkHistory } from "./UploadWorkHistory";
import ReviewForm from "../../components/ReviewForm/ReviewForm";
import TopNavigationBar from "../TopNavigationBar";
import {
  LICENCED_EXEMPT,
  LICENCED_UNDER_BROKERAGE,
  NOT_LICENCED
} from "../../redux/slice/primaryAgencyApplicationSlice";
import { setCurrentAgentMortgageProperty } from "../../redux/slice/additionalApplicationsSlice";
import { saveBusinessLicenceApplicationData } from "../../redux/slice/businessLicenceApplicationSlice";
import { ERROR_STRING } from "../../app/errors";
import { fullName, humanReadableDate } from "../../app/utils";
import { currentAgentApplicationSelector } from "../../redux/selector/collectorApplicationSelector";
import { applicationTypeFromLicenceSubType } from "../../redux/slice/applicationsUtils";
import { fetchDocumentData } from "../../redux/slice/supportingDocumentsSlice";

const MORTGAGE_APPLICATION_PROPERTY = "userApplicationReviewComplete";

export const BrokerApplicationReview = (props) => {
  const dispatch = useDispatch();
  const [applicationSaveError, setApplicationSaveError] = useState(false);
  const [openDocumentViewer, setOpenDocumentViewer] = useState(false);
  const [documentViewerDocument, setDocumentViewerDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const agentApplication = useSelector(currentAgentApplicationSelector);
  const licencingSubType =
    agentApplication?.licencingSubType ?? LICENCE_SUB_TYPE_MORTGAGE_BROKER_NEW_APPLICATION;
  const isPrincipalBroker =
    licencingSubType === LICENCE_SUB_TYPE_PRINCIPAL_BROKER_NEW_APPLICATION ||
    licencingSubType === LICENCE_SUB_TYPE_PRINCIPAL_BROKER_RENEWAL_APPLICATION;
  const isAssociateBroker =
    licencingSubType === LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_NEW_APPLICATION;
  const isNewBroker =
    licencingSubType === LICENCE_SUB_TYPE_MORTGAGE_BROKER_NEW_APPLICATION ||
    licencingSubType === LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_NEW_APPLICATION;

  const mraApplicationState = agentApplication.businessLicenceData ?? {};
  const cepRipExpiryDate = agentApplication?.cepRipExpiryDate;
  const currentlyBrokerInNS = mraApplicationState?.currentlyBrokerInNS;
  const currentlyLicenceNumber = mraApplicationState?.brokerLicenceNumber;

  const documents = agentApplication?.documents;
  const {
    previouslyLicencedStatus,
    mblraLicenceNumber,
    licenceHistoryData,
    confirmCompletionRegulatoryInfo,
    confirmRequiredCourseCompletion,
    hasRequiredEducationQualification,
    hasRequiredEducationCertificate,
    confirmEducationQualificationCompletion,
    licenceAnotherProvince,
    personData,
    workHistoryData,
  } = mraApplicationState;

  const applicationType = applicationTypeFromLicenceSubType(licencingSubType);
  const applicationMode = APPLICATION_MODE_NEW;
  // const personData = agentApplication?.businessLicenceData?.personData ?? {};

  const uploadCriminalRecordCheck = useMemo(
    () =>
      documents.filter(
        (document) =>
          document.documentType === DOCUMENT_TYPE_CRIMINAL_RECORD &&
          document.changeType !== "DELETE"
      ),
    [documents]
  );

  const [editComponent, setEditComponent] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const COMPONENT_KEYS = {
    APPLICANT_NAME: "applicantName",
    APPLICANT_CONTACT: "applicantContact",
    CURRENT_MORTGAGE_LICENCE: "currentMortgageLicence",
    LICENCE_EXEMPT_PROVINCE: "licenceExemptProvince",
    BROKER_EDUCATION: "brokerEducation",
    ASSOCIATE_APPLICANT_NAME: "associateApplicantName",
    ASSOCIATE_CRIMINAL_RECORD: "associateCriminalRecord",
    ASSOCIATE_WORK_HISTORY: "associateWorkHistory"
  };

  const MainSeparator = () => {
    return (
      <hr className="bottomMarginNormal" style={{ borderColor: "#777", borderWidth: "3px" }} />
    );
  };

  const SubSeparator = () => {
    return (
      <hr
        className="bottomMarginNormal"
        style={{
          borderColor: "#0058a466",
          borderWidth: "3px",
          width: "60%",
          marginLeft: "0px"
        }}
      />
    );
  };

  const documentReviewComponent = useCallback(
    (file) => {
      const fileComponents = [];

      if (file.documentContentType === DOCUMENT_CONTENT_TYPE_PDF) {
        fileComponents.push(
          <div style={{ maxWidth: "40rem", overflow: "hidden" }}>
            <DocumentViewerFileNameLink
              id={"document-view-link-" + file.fileName}
              key={"document-view-link-" + file.fileName}
              file={file}
              onClick={() => {
                if (file.data) {
                  let fileDataUrl = convertFileDataForUrl(file.data, file.documentContentType);
                  setDocumentViewerDocument(fileDataUrl);
                  setSelectedFile(file);
                  setOpenDocumentViewer(true);
                } else {
                  // download file data and then show it
                  dispatch(fetchDocumentData(file.guid))
                    .then((downloadFile) => {
                      if (downloadFile) {
                        let fileDataUrl = convertFileDataForUrl(
                          downloadFile.data,
                          downloadFile.documentContentType
                        );
                        setDocumentViewerDocument(fileDataUrl);
                        setSelectedFile(downloadFile);
                        setOpenDocumentViewer(true);
                      }
                    })
                    .catch((error) => {
                      console.error(error);
                    });
                }
              }}
            />
          </div>
        );
      } else {
        fileComponents.push(
          <DownloadFileNameLink
            id={"download-link-" + file.fileName}
            componentKey={"download-link-" + file.fileName}
            file={file}
          />
        );
      }

      return fileComponents;
    },
    [dispatch, setDocumentViewerDocument, setSelectedFile, setOpenDocumentViewer]
  );

  // const doc1 = (file) => {
  //   const fileComponents = [];

  //   if (file.documentContentType === DOCUMENT_CONTENT_TYPE_PDF) {
  //     fileComponents.push(
  //       <div style={{ maxWidth: "40rem", overflow: "hidden" }}>
  //         <DocumentViewerFileNameLink
  //           id={"document-view-link-" + file.fileName}
  //           key={"document-view-link-" + file.fileName}
  //           file={file}
  //           onClick={() => {
  //             if (file.data) {
  //               let fileDataUrl = convertFileDataForUrl(
  //                 file.data,
  //                 file.documentContentType
  //               );
  //               setDocumentViewerDocument(fileDataUrl);
  //               setSelectedFile(file);
  //               setOpenDocumentViewer(true);
  //             } else {
  //               // download file data and then show it
  //               dispatch(fetchDocumentData(file.guid))
  //                 .then(downloadFile => {
  //                     if(downloadFile) {
  //                         let fileDataUrl = convertFileDataForUrl(downloadFile.data, downloadFile.documentContentType)
  //                         setDocumentViewerDocument(fileDataUrl)
  //                         setSelectedFile(downloadFile)
  //                         setOpenDocumentViewer(true)
  //                     }
  //                 })
  //                 .catch((error) => {
  //                     console.error(error)
  //                 })
  //             }
  //           }}
  //         />
  //       </div>
  //     );
  //   } else {
  //     fileComponents.push(
  //       <DownloadFileNameLink
  //         id={"download-link-" + file.fileName}
  //         componentKey={"download-link-" + file.fileName}
  //         file={file}
  //       />
  //     );
  //   }

  //   return fileComponents;
  // };

  const workHistoryReviewData = useMemo(() => {
    const historyDetails = (history) => {
      return {
        "Start date": humanReadableDate(history.startDate),
        Duration: history.duration ?? "",
        Description: history.description ?? ""
      };
    };

    const HistoryComponents = workHistoryData.map((workHistory, index) => {
      return (
        <>
          <ReviewForm fullSize data={historyDetails(workHistory)} />
          {index < workHistoryData.length - 1 && <SubSeparator />}
        </>
      );
    });

    const experienceLetters = documents?.filter(
      (d) => d.documentType === DOCUMENT_TYPE_WORK_EXPERIENCE && d.changeType !== "DELETE"
    );
    const ExpLetterComponents = experienceLetters.map((d, index) => {
      return (
        <>
          <ReviewForm
            fullSize
            data={{
              "Uploaded document": documentReviewComponent(d)
            }}
          />
          {index < experienceLetters.length - 1 && <SubSeparator />}
        </>
      );
    });

    return (
      <>
        {HistoryComponents}
        {ExpLetterComponents}
      </>
    );
  }, [workHistoryData, documents, documentReviewComponent]);

  const brokerCourseReviewData = useMemo(() => {
    const descText = `I confirm that ${
      isPrincipalBroker ? "I" : "the applicant"
    } will complete the Nova Scotia Mortgage Broker course before the date ${
      isPrincipalBroker ? "my" : "the applicant's"
    } new licence expires`;
    const brokerCourseDocuments = documents?.filter(
      (d) => d.documentType === DOCUMENT_TYPE_BROKER_COURSE_CERTIFICATE && d.changeType !== "DELETE"
    );
    const BrokerCourseCertificateComponents = brokerCourseDocuments.map((d, index) => {
      return (
        <>
          <ReviewForm
            fullSize
            data={{
              "Uploaded document": documentReviewComponent(d)
            }}
          />
          {index < brokerCourseDocuments.length - 1 && <SubSeparator />}
        </>
      );
    });
    return (
      <>
        <ReviewForm
          fullSize
          data={{
            [descText]: confirmRequiredCourseCompletion ? "Yes" : "No"
          }}
        />
        {BrokerCourseCertificateComponents}
      </>
    );
  }, [isPrincipalBroker, confirmRequiredCourseCompletion, documents, documentReviewComponent]);

  const previousLicenceReviewData = useMemo(() => {
    const textMapping = {
      LICENCED_INDIVIDUAL: "Mortgage Broker Licence",
      LICENCED_UNDER_BROKERAGE: `Mortgage Broker Licence`,
      LICENCED_EXEMPT:
        "Mortgage Broker Licence - you brokered mortgages for a federally regulated financial institution for at least 24 out of the last 36 months immediately before the date of your application.",
      NOT_LICENCED: "Associate Mortgage Broker Licence"
    };

    return {
      "What type of licence are you applying for?": textMapping[previouslyLicencedStatus] ?? ""
    };
  }, [previouslyLicencedStatus]);

  const educationCertificateReviewData = useMemo(() => {
    const k1 = "Has the applicant completed the education requirement?";
    const k2 = "Has the applicant provided a certificate of completing the education requirement?";

    let reviewData = {};

    const addProperty = (obj, k, v) => {
      return Object.defineProperty(obj, k, {
        value: v,
        enumerable: true,
        writable: true
      });
    };

    const exemptText = isAssociateBroker
      ? "This applicant is exempt because they have a current Associate Mortgage Broker Licence (or equivalent) from an exempt province."
      : "The Requirement is exempt since this applicant has a current Mortgage Broker Licence from an exempt province.";

    if (licenceAnotherProvince) {
      reviewData = addProperty(
        reviewData,
        k1,
        exemptText
      );
    } else {
      if (hasRequiredEducationQualification) {
        // reviewData = addProperty(reviewData, k1, "Yes")
      } else {
        if (confirmEducationQualificationCompletion) {
          reviewData = addProperty(
            reviewData,
            k1,
            "No, the principal broker confirmed the applicant will complete the requirement within 1 year."
          );
        } else {
          reviewData = addProperty(reviewData, k1, "No");
        }
      }

      if (hasRequiredEducationCertificate) {
        // reviewData = addProperty(reviewData,k2,"Yes")
        if (isAssociateBroker) {
          const associateBrokerQuestion =
            "Has the applicant completed the Nova Scotia Associate Mortgage Broker Course?";
          reviewData = addProperty(reviewData, associateBrokerQuestion, "Yes");
        }
        
        reviewData = addProperty(reviewData, "Education Program valid until date", humanReadableDate(cepRipExpiryDate) ?? "N/A");
        
        const educationCertificates = documents?.filter(
          (document) =>
            document.documentType === DOCUMENT_TYPE_EDUCATION_CERTIFICATE &&
            document.changeType !== "DELETE"
        );
        if (educationCertificates?.length) {
          educationCertificates.forEach((cert) => {
            const fileC = documentReviewComponent(cert);
            reviewData = addProperty(reviewData, "Uploaded certificate", fileC);
          });
        } else {
          reviewData = addProperty(reviewData, "Uploaded certificate", "");
        }
      } else {
        if (confirmEducationQualificationCompletion) {
          reviewData = addProperty(
            reviewData,
            k2,
            "No, the principal broker confirmed the applicant will complete the requirement within 1 year."
          );
        } else {
          reviewData = addProperty(reviewData, k2, "No");
        }
      }
    }

    return reviewData;
  }, [
    hasRequiredEducationQualification,
    hasRequiredEducationCertificate,
    confirmEducationQualificationCompletion,
    licenceAnotherProvince,
    documents,
    documentReviewComponent,
    isAssociateBroker,
    cepRipExpiryDate
  ]);

  const titleForComponent = (componentKey) => {
    switch (componentKey) {
      case COMPONENT_KEYS.APPLICANT_NAME:
        return "Applicant Name";
      case COMPONENT_KEYS.APPLICANT_CONTACT:
        return "Applicant Contact";
      case COMPONENT_KEYS.CURRENT_MORTGAGE_LICENCE:
        return "Current Mortgage Licence";
      case COMPONENT_KEYS.LICENCE_EXEMPT_PROVINCE:
        return "Licence Exempt Province";
      case COMPONENT_KEYS.BROKER_EDUCATION:
        return "Broker Education";
      case COMPONENT_KEYS.ASSOCIATE_APPLICANT_NAME:
        return "Applicant Information";
      case COMPONENT_KEYS.ASSOCIATE_CRIMINAL_RECORD:
        return "Applicant Criminal Record Check";
      case COMPONENT_KEYS.ASSOCIATE_WORK_HISTORY:
        return "Applicant Work History";
      default:
        return "";
    }
  };

  /*=== click handlers == */
  const handleEditBrokerApplicant = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.APPLICANT_NAME);
  };

  const handleEditBrokerContact = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.APPLICANT_CONTACT);
  };

  const handleEditCurrentMortgageLicence = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.CURRENT_MORTGAGE_LICENCE);
  };

  const handleEditBrokerEducation = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.BROKER_EDUCATION);
  };

  const handleEditAssociateBrokerApplicant = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.ASSOCIATE_APPLICANT_NAME);
  };

  const handleEditAssociateCriminalRecord = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.ASSOCIATE_CRIMINAL_RECORD);
  };

  // const handleEditAssociateWorkHistory = () => {
  //   setShowEditDialog(true);
  //   setEditComponent(COMPONENT_KEYS.ASSOCIATE_WORK_HISTORY);
  // }


  let stepCounter = 1;

  return (
    <>
      <div className={"container"}>
        <TopNavigationBar
          {...props}
          applicationType={applicationType}
          applicationMode={applicationMode}
        />
        <div className="row">
          <div className="col-xs-12 col-lg-9">
            <Header id="broker-application-review-header" header="Application Review" />
            {isPrincipalBroker && (
              <Paragraph
                id="broker-application-review-text"
                text={[
                  "Review the information you provided and continue to the next step to provide information about your brokerage."
                ]}
              />
            )}
          </div>
        </div>

        {null &&
          !isPrincipalBroker &&
          !licenceAnotherProvince &&
          !hasRequiredEducationCertificate && (
            <div className="row">
              <div style={{ border: "solid red" }} className="col-xs-12 col-lg-9">
                <h2>Critical Information is missing</h2>
                <p style={{ color: "red", margin: "0 0 0 0" }}>
                  A copy of the education completion is not provided
                </p>
                <p style={{ color: "red" }}>Upload this document</p>
              </div>
            </div>
          )}

        <div className="timeline row">
          <ol>
            <li className="timeline-item col-md-12">
              <div className="step-content col-md-12 col-xs-12">
                <span className="step-counter step-counter-circle">{stepCounter++}</span>
                <div className="row">
                  <div className="col-xs-10">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["<h3>Application<h3>"]}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-3">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["Licence Type"]}
                    />
                  </div>
                  <div className="col-lg-3">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={[
                        isPrincipalBroker
                          ? "<i>Mortgage Principal Broker Licence</i>"
                          : isAssociateBroker
                            ? "<i>Associate Mortgage Broker Licence</i>"
                            : "<i>Mortgage Broker Licence</i>"
                      ]}
                    />
                  </div>
                </div>
              </div>
            </li>

            {isNewBroker && currentlyBrokerInNS && (
              <li className="timeline-item col-md-12">
                <div className="step-content col-md-12 col-xs-12">
                  <MainSeparator />
                  <span className="step-counter step-counter-circle">{stepCounter++}</span>
                  <div>
                    <ReviewForm fullSize data={{}} />
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={[
                        "<h3>Current Mortgage Broker or Associate Mortgage Broker Licence Number in Nova Scotia?<h3>"
                      ]}
                    />
                  </div>
                  <div className="row">
                    <div className="col-lg-3">
                      <Paragraph
                        style={{ paddingTop: "10px" }}
                        id="broker-application-review-text"
                        text={["Licence Number"]}
                      />
                    </div>
                    <div className="col-lg-3">
                      <Paragraph
                        style={{ paddingTop: "10px" }}
                        id="broker-application-review-text"
                        text={[currentlyLicenceNumber]}
                      />
                    </div>
                  </div>
                </div>
              </li>
            )}

            <li className="timeline-item col-md-12">
              <div className="step-content col-md-12 col-xs-12">
                <MainSeparator />
                <span className="step-counter step-counter-circle">{stepCounter++}</span>
                {!isPrincipalBroker ? (
                  <div className="row">
                    <div className="col-lg-11">
                      <Paragraph
                        style={{ paddingTop: "10px" }}
                        id="broker-application-review-text"
                        text={["<h3>Applicant information</h3>"]}
                      />
                      <ReviewForm
                        fullSize
                        data={{
                          Name: `${fullName(personData)}` ?? "",
                          "Home Address": personData?.addressData?.address1 ?? "",
                          City: personData?.addressData?.cityName ?? "",
                          Province: personData?.addressData?.stateName ?? "",
                          "Postal code": personData?.addressData?.postalCode ?? "",
                          "Phone number": personData?.phoneNumber ?? "",
                          "Alternate phone number": personData?.phoneNumber ?? "",
                          Email: personData?.email ?? ""
                        }}
                      />
                    </div>
                    <div className="col-lg-1">
                      <Button
                        style={{ marginTop: "10px" }}
                        buttonType={"regular_primary"}
                        text={"Edit"}
                        onClick={handleEditAssociateBrokerApplicant}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="row">
                      <div className="col-lg-11">
                        <Paragraph
                          style={{ paddingTop: "10px" }}
                          id="broker-application-review-text"
                          text={["<h3>Applicant<h3>"]}
                        />
                        <ReviewForm
                          fullSize
                          data={{
                            "First name": personData?.firstName ?? "",
                            "Last name": personData?.lastName ?? ""
                          }}
                        />
                      </div>
                      <div className="col-lg-1">
                        <Button
                          style={{ marginTop: "10px" }}
                          buttonType={"regular_primary"}
                          text={"Edit"}
                          onClick={handleEditBrokerApplicant}
                        />
                      </div>
                    </div>
                    <MainSeparator />
                  </>
                )}
              </div>
            </li>

            {isPrincipalBroker && (
              <li className="timeline-item col-md-12">
                <div className="step-content col-md-12 col-xs-12">
                  <div className="row">
                    <div className="col-lg-11">
                      <Paragraph
                        style={{ paddingTop: "10px" }}
                        id="broker-application-review-text"
                        text={["<h3>Applicant Information<h3>"]}
                      />
                      <ReviewForm
                        fullSize
                        data={{
                          "Home Address": personData?.addressData?.address1 ?? "",
                          "City/Town": personData?.addressData?.cityName ?? "",
                          Province: personData?.addressData?.stateName ?? "",
                          "Postal code": personData?.addressData?.postalCode ?? "",
                          "Phone number": personData?.phoneNumber ?? "",
                          "Alternate phone number": personData?.alternatePhoneNumber ?? "",
                          Email: personData?.email ?? ""
                        }}
                      />
                    </div>
                    <div className="col-lg-1">
                      <Button
                        style={{ marginTop: "10px" }}
                        buttonType={"regular_primary"}
                        text={"Edit"}
                        onClick={handleEditBrokerContact}
                      />
                    </div>
                  </div>
                  <div className="row"></div>
                </div>
              </li>
            )}

            {previouslyLicencedStatus && (
              <li className="timeline-item col-md-12">
                <div className="step-content col-md-12 col-xs-12">
                  <MainSeparator />
                  <span className="step-counter step-counter-circle">{stepCounter++}</span>
                  <div className="row">
                    <div className="col-lg-11">
                      <Paragraph
                        style={{ paddingTop: "10px" }}
                        id="broker-application-review-text"
                        text={[
                          `<h3>${
                            previouslyLicencedStatus === LICENCED_EXEMPT
                              ? "Work History and Mortgage Broker Course"
                              : "Current Mortgage Broker Licences"
                          }<h3>`
                        ]}
                      />
                      <ReviewForm fullSize data={previousLicenceReviewData} />
                    </div>
                    <div className="col-lg-1">
                      <Button
                        style={{ marginTop: "10px" }}
                        buttonType={"regular_primary"}
                        text={"Edit"}
                        onClick={handleEditCurrentMortgageLicence}
                      />
                    </div>
                  </div>

                  {previouslyLicencedStatus === LICENCED_EXEMPT && (
                    <div className="row">
                      <SubSeparator />
                      <div className="col-lg-11">
                        <Paragraph
                          style={{ paddingTop: "10px" }}
                          id="broker-application-review-text"
                          text={["<h3>Work history<h3>"]}
                        />
                        {workHistoryReviewData}
                      </div>
                      <SubSeparator />
                      <div className="col-lg-11">
                        <Paragraph
                          style={{ paddingTop: "10px" }}
                          id="broker-application-review-text"
                          text={["<h3>Nova Scotia Mortgage Broker Course<h3>"]}
                        />
                        {brokerCourseReviewData}
                      </div>
                    </div>
                  )}

                  {!currentlyBrokerInNS &&
                    (previouslyLicencedStatus === LICENCED_UNDER_BROKERAGE ||
                      (previouslyLicencedStatus === NOT_LICENCED && licenceAnotherProvince)) && (
                      <>
                        <SubSeparator />
                        <div className="row">
                          <div className="col-lg-11">
                            <Paragraph
                              style={{ paddingTop: "10px" }}
                              id="broker-application-review-text"
                              text={["<h4>Licenses from exempt provinces?</h4>"]}
                            />
                            <ReviewForm
                              fullSize
                              data={{
                                [previouslyLicencedStatus === NOT_LICENCED
                                  ? "Does the applicant have a current associate mortgage broker licence (or equivalent) from the exempt provinces?"
                                  : "Have current mortgage broker licence from the exempt provinces?"]:
                                  licenceAnotherProvince ? "Yes" : "No"
                              }}
                            />
                          </div>
                        </div>

                        {licenceAnotherProvince && (
                          <>
                            <SubSeparator />
                            <div className="row">
                              <div className="col-lg-11">
                                <Paragraph
                                  style={{ paddingTop: "10px" }}
                                  id="broker-application-review-text"
                                  text={["<h4>Licence details from exempt provinces<h4>"]}
                                />
                                <ReviewForm
                                  fullSize
                                  data={{
                                    Province: licenceHistoryData?.province ?? "",
                                    "Licence number": licenceHistoryData?.licenceNumber ?? "",
                                    "Expiry date": licenceHistoryData?.endDate
                                      ? humanReadableDate(licenceHistoryData?.endDate)
                                      : ""
                                  }}
                                />
                              </div>
                            </div>
                          </>
                        )}

                        {!licenceAnotherProvince && (
                          <>
                            <SubSeparator />
                            <div className="row">
                              <div className="col-lg-11">
                                <Paragraph
                                  style={{ paddingTop: "10px" }}
                                  id="broker-application-review-text"
                                  text={[
                                    "<h4>Provide your associate mortgage broker licence number under MRA<h4>"
                                  ]}
                                />
                                <ReviewForm
                                  fullSize
                                  data={{
                                    "Licence number": mblraLicenceNumber ?? ""
                                  }}
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )}
                </div>
              </li>
            )}

            <li className="timeline-item col-md-12">
              <div className="step-content col-md-12 col-xs-12">
                <MainSeparator />
                <span className="step-counter step-counter-circle">{stepCounter++}</span>
                {!isAssociateBroker && (
                  <>
                    <div className="row">
                      <div className="col-lg-11">
                        <Paragraph
                          style={{ paddingTop: "10px" }}
                          id="broker-application-review-text"
                          text={["<h3>Education<h3>", "<h4>Regulatory Information Program</h4>"]}
                        />
                        <ReviewForm
                          fullSize
                          data={{
                            "Completion of the Regulatory Information Program":
                              confirmCompletionRegulatoryInfo ? "Yes" : "No",
                            "Regulatory Information Program valid until date":
                              confirmCompletionRegulatoryInfo && agentApplication?.cepRipExpiryDate
                                ? humanReadableDate(agentApplication?.cepRipExpiryDate)
                                : "---"
                          }}
                        />
                      </div>
                      <div className="col-lg-1">
                        <Button
                          style={{ marginTop: "10px" }}
                          buttonType={"regular_primary"}
                          text={"Edit"}
                          onClick={handleEditBrokerEducation}
                        />
                      </div>
                    </div>
                  </>
                )}
                {isAssociateBroker || //Always for ASM
                (!currentlyBrokerInNS && //When mortgage broker don't have NS licence number
                  (!isPrincipalBroker || previouslyLicencedStatus === LICENCED_UNDER_BROKERAGE)) ? (
                  <>
                    {!isAssociateBroker && <SubSeparator />}

                    <div className="row">
                      <div className="col-lg-11">
                        <Paragraph
                          style={{ paddingTop: "10px" }}
                          id="broker-application-review-text"
                          text={[
                            `<h4>Nova Scotia ${
                              isAssociateBroker
                                ? "Associate Broker Course"
                                : "Mortgage Broker Education Program"
                            }</h4>`
                          ]}
                        />
                        <ReviewForm fullSize data={educationCertificateReviewData} />
                      </div>
                      {isAssociateBroker && (
                        <div className="col-lg-1">
                          <Button
                            style={{ marginTop: "10px" }}
                            buttonType={"regular_primary"}
                            text={"Edit"}
                            onClick={handleEditBrokerEducation}
                          />
                        </div>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            </li>

            {!isPrincipalBroker && (
              <li className="timeline-item col-md-12">
                <div className="step-content col-md-12 col-xs-12">
                  <MainSeparator />
                  <span className="step-counter step-counter-circle">{stepCounter++}</span>
                  <div className="row">
                    <div className="col-lg-11">
                      <Paragraph
                        style={{ paddingTop: "10px" }}
                        id="broker-application-review-text"
                        text={["<h3>Criminal Record<h3>"]}
                      />
                      {Array.isArray(uploadCriminalRecordCheck) &&
                        uploadCriminalRecordCheck.map((element, index) => (
                          <>
                            <ReviewForm
                              fullSize
                              data={{
                                File: documentReviewComponent(element)
                              }}
                            />
                            {index < uploadCriminalRecordCheck.length - 1 && <SubSeparator />}
                          </>
                        ))}
                    </div>
                    <div className="col-lg-1">
                      <Button
                        style={{ marginTop: "10px" }}
                        buttonType={"regular_primary"}
                        text={"Edit"}
                        onClick={handleEditAssociateCriminalRecord}
                      />
                    </div>
                  </div>
                </div>
              </li>
            )}
          </ol>
        </div>

        <div className="row">
          <div className={"col-lg-4" + applicationSaveError ? " has-error" : ""}>
            <Button
              id="complete-mortgage-education-continue-button"
              style={{ margin: "0 10px 10px 0" }}
              buttonType={"regular_primary"}
              text={"Save and next"}
              onClick={() => {
                // setting this flag will make the application elibigle for the payment.
                // this flag will be set true in the "confirmation" screen for the
                // individual broker and associate broker
                if (isPrincipalBroker) {
                  dispatch(
                    setCurrentAgentMortgageProperty({
                      key: MORTGAGE_APPLICATION_PROPERTY,
                      value: true
                    })
                  );
                }

                const nextPath = !isPrincipalBroker
                  ? ROUTE_PATH.MRA_ASSOCIATE_CONFIRMATION
                  : ROUTE_PATH.MRA_BROKER_NEXT_STEPS;
                const promiseArray = [];
                const p1 = dispatch(
                  saveBusinessLicenceApplicationData(
                    APPLICATION_TYPE_MORTGAGE_PRINCIPAL_BROKER_LICENCE,
                    applicationMode
                  )
                );
                promiseArray.push(p1);

                // only in case the review is for principal broker
                if (isPrincipalBroker) {
                  // save the main agency application, just in case
                  // the details like principal broker name, etc. is
                  // modified
                  const p2 = dispatch(
                    saveBusinessLicenceApplicationData(
                      APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE,
                      applicationMode
                    )
                  );
                  promiseArray.push(p2);
                }

                Promise.all(promiseArray)
                  .then((values) => {
                    // On to the next item
                    dispatch(push(`${ROUTE_PATH.MRA_APP}${nextPath}`));
                  })
                  .catch((err) => {
                    setApplicationSaveError(true);
                  });
              }}
            />
            {applicationSaveError && ErrorMark()}
            {applicationSaveError && (
              <div className="ns-error-message help-block topMarginNormal">
                <span className="ns-visually-hidden">Error:</span>
                {ERROR_STRING.APPLICATION_SAVE_ERROR}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        appElement={document.getElementById("root")}
        isOpen={showEditDialog}
        contentLabel="brokerReviewEditDialog"
        onRequestClose={() => {}}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        className="modalDialogContent"
        closeTimeoutMS={150}
      >
        <div className="row pull-right">
          <div
            className="col-lg-1 closeButton"
            onClick={() => {
              setShowEditDialog(false);
            }}
          >
            <i className="fa fa-2x fa-times"></i>
          </div>
        </div>
        <Header header={"Edit current licence information"} />
        <Paragraph
          text={[
            "Editing the current licence information will impact other sections of this application. Based on the changes you make, you may need to update other sections too.",
            "Continue to edit Current Licence Information?"
          ]}
        />
        <div className="row">
          <div className="col-lg-4">
            <Button
              buttonType="regular_primary"
              text="Yes, go back and edit"
              onClick={() => {
                let nextPage = ROUTE_PATH.MRA_SELECT_PRINCIPAL_BROKER_FROM_LIST;

                // start from the applicant infomation page for broker or
                // associate broker applications
                if (!isPrincipalBroker) {
                  nextPage = ROUTE_PATH.MRA_ASSOCIATE_APPLICANT_INFO;
                }

                dispatch(push(`${ROUTE_PATH.MRA_APP}${nextPage}`));
              }}
            />
          </div>
          <div className="col-lg-2">
            <NavigationLinkButton
              text="Cancel"
              onClick={() => {
                setShowEditDialog(false);
              }}
            />
          </div>
        </div>
      </Modal>
      <Modal
        appElement={document.getElementById("root")}
        isOpen={0 && showEditDialog}
        contentLabel="editDetailsDialog"
        onRequestClose={() => {
          setShowEditDialog(false);
        }}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        className="modalDialogContent editDetailsModalDialog"
        closeTimeoutMS={150}
      >
        <div className="row">
          <div className="col-11">
            <div className="row">
              <div className="col-12">
                <h2>{titleForComponent(editComponent)}</h2>
                <MainSeparator />
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.APPLICANT_NAME && (
                  <MraSelectPrincipalBroker
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.APPLICANT_CONTACT && (
                  <ProvideBrokerContactInformation
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.CURRENT_MORTGAGE_LICENCE && (
                  <PreviouslyLicencedMortgageBroker
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.LICENCE_EXEMPT_PROVINCE && (
                  <MortgageBrokerInfo
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.BROKER_EDUCATION && (
                  <ConfirmCompletionRegulatoryInfo
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.ASSOCIATE_APPLICANT_NAME && (
                  <AssociateBrokerAddApplicant
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.ASSOCIATE_CRIMINAL_RECORD && (
                  <UploadCriminalRecordCheck
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.ASSOCIATE_WORK_HISTORY && (
                  <UploadWorkHistory
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="col-1">
            <div
              className="closeButton pull-right"
              onClick={() => {
                setShowEditDialog(false);
              }}
            >
              <i className="fa fa-2x fa-times"></i>
            </div>
          </div>
        </div>
      </Modal>

      <DocumentViewer
        fileData={documentViewerDocument}
        fileName={selectedFile?.filename}
        isOpen={openDocumentViewer}
        handleClose={() => setOpenDocumentViewer(false)}
      />
    </>
  );
};
